import { PlusIcon } from '@langchain/untitled-ui-icons';
import { Button, LinearProgress } from '@mui/joy';

import { ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useAnnotationQueues } from '@/hooks/useSwr';
import { AnnotationQueueSchema } from '@/types/schema';
import { localTime } from '@/utils/utc-time';

import { ShowAllPickerExtraItem } from '../ResourceTagFilter/ShowAllPickerExtraItem';

const AnnotationQueuePopoverItem = (props: {
  title?: string;
  subtitle?: ReactNode;
}) => {
  return (
    <div className="inline-flex w-[480px] items-center justify-start rounded-lg pl-3.5 pr-2.5">
      <div className="inline-flex shrink grow basis-0 flex-col items-start justify-center gap-1">
        {props.title && (
          <div className="inline-flex items-center justify-start gap-1.5 self-stretch">
            <div className="text-sm font-semibold leading-normal">
              {props.title}
            </div>
          </div>
        )}
        {props.subtitle && (
          <div className="inline-flex items-baseline justify-start gap-1.5">
            <div className="text-xs font-normal leading-none text-slate-400">
              {props.subtitle}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const AddToAnnotationQueuePopoverContent = (props: {
  handleSendToAnnotationQueue: (aq: AnnotationQueueSchema) => void;
  openNewAnnotationQueueModal: () => void;
  title?: string;
}) => {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 250);

  const [showAll, setShowAll] = useState(false);
  const { selectedTagIds } = useStoredResourceTags();
  const { datasetId } = useParams();

  const { data: annotationQueues, isLoading } = useAnnotationQueues({
    name_contains: debouncedSearch === '' ? undefined : debouncedSearch,
    tag_value_id: showAll ? undefined : selectedTagIds,
    dataset_id: datasetId,
  });

  const annotationQueuesToDisplay: AnnotationQueueSchema[] =
    annotationQueues ?? [];

  return (
    <Command shouldFilter={false} className="overflow-hidden">
      <div className="overflow-y-auto overflow-x-hidden">
        <div className="inline-flex w-full items-center justify-between py-3 pr-3">
          <div className="px-4 text-sm font-semibold leading-normal">
            {props.title ?? 'Select Annotation Queue'}
          </div>
          <Button
            data-testid="new-annotation-queue-button"
            variant="outlined"
            color="neutral"
            size="sm"
            startDecorator={<PlusIcon className="size-4" />}
            onClick={props.openNewAnnotationQueueModal}
          >
            New
          </Button>
        </div>
        <div className="px-3">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search Queues..."
            className="text-sm outline-none"
          />
        </div>
        <div className="mt-3">
          <CommandList>
            {annotationQueues ? (
              <CommandGroup
                className="m-0 [&_[cmdk-group-heading]]:bg-secondary [&_[cmdk-group-heading]]:px-5 [&_[cmdk-group-items]]:py-1.5"
                heading={
                  <span className="text-xs font-medium uppercase tracking-wide text-tertiary">
                    Recently Updated
                  </span>
                }
              >
                {isLoading ? (
                  <LinearProgress />
                ) : (
                  <>
                    {annotationQueuesToDisplay.map((aq, index) => {
                      const momentLastUpdated = localTime(aq.updated_at);

                      const lastUpdatedString =
                        'Updated ' + momentLastUpdated.fromNow();
                      return (
                        <CommandItem
                          key={index}
                          className="mx-1.5 flex cursor-pointer items-center px-0 py-3"
                          onSelect={() => {
                            props.handleSendToAnnotationQueue(aq);
                          }}
                        >
                          <AnnotationQueuePopoverItem
                            title={aq.name}
                            subtitle={lastUpdatedString}
                          />
                        </CommandItem>
                      );
                    })}

                    <div className="mx-1.5 flex min-h-10 items-center py-3">
                      <AnnotationQueuePopoverItem
                        subtitle={
                          <ShowAllPickerExtraItem
                            showAll={showAll}
                            resources={annotationQueuesToDisplay}
                            setShowAll={setShowAll}
                            hasSelectedTags={selectedTagIds.length > 0}
                            pickerType="Annotation Queue"
                          />
                        }
                      />
                    </div>
                  </>
                )}
              </CommandGroup>
            ) : (
              <LinearProgress />
            )}
          </CommandList>
        </div>
      </div>
    </Command>
  );
};
